import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowRight,
  Clock3,
  Copy,
  Download,
  Inbox,
  LockKeyhole,
  Mail,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import type {
  GuestMailboxSession,
  MailAttachment,
  MailDomainSummary,
  MailMessage,
  Mailbox,
  MailboxWorkspace,
  ShortLink,
} from "../../shared/types";
import { MailMessageReader } from "../components/mail-message-reader";
import { Button, Dialog, Loading, Tag, useToast } from "../components/ui";
import { useSession } from "../context/session";
import { api, jsonBody } from "../lib/api";

const guestSessionKey = "g2al.guest-mailbox.v1";
const recentLinkKey = "g2al.recent-links.v1";
const mailboxStyleKey = "g2al.mailbox-alphanumeric.v1";

interface StoredMailboxSession {
  id: string;
  token: string | null;
  recoveryCode: string | null;
}

function readStoredMailbox(): StoredMailboxSession | null {
  try {
    const raw = sessionStorage.getItem(guestSessionKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || typeof Reflect.get(parsed, "id") !== "string") return null;
    return {
      id: Reflect.get(parsed, "id") as string,
      token: typeof Reflect.get(parsed, "token") === "string" ? Reflect.get(parsed, "token") as string : null,
      recoveryCode: typeof Reflect.get(parsed, "recoveryCode") === "string" ? Reflect.get(parsed, "recoveryCode") as string : null,
    };
  } catch {
    return null;
  }
}

function saveStoredMailbox(value: StoredMailboxSession): void {
  try {
    sessionStorage.setItem(guestSessionKey, JSON.stringify(value));
  } catch {
    // The inbox remains usable for this render even when storage is unavailable.
  }
}

function readRecentLinks(): ShortLink[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(recentLinkKey) ?? "[]");
    return Array.isArray(parsed) ? parsed.slice(0, 2) as ShortLink[] : [];
  } catch {
    return [];
  }
}

function readAlphanumericPreference(): boolean {
  try {
    const stored = localStorage.getItem(mailboxStyleKey);
    return stored === null ? true : stored === "true";
  } catch {
    return true;
  }
}

function storeRecentLink(link: ShortLink): ShortLink[] {
  const next = [link, ...readRecentLinks().filter((item) => item.id !== link.id)].slice(0, 2);
  try {
    localStorage.setItem(recentLinkKey, JSON.stringify(next));
  } catch {
    // Recent items are only a convenience.
  }
  return next;
}

async function copyText(value: string): Promise<void> {
  await navigator.clipboard.writeText(value);
}

function remainingLabel(expiresAt: string | null, now: number): string {
  if (!expiresAt) return "永不过期";
  if (now === 0) return "同步中";
  const difference = Math.max(0, Date.parse(expiresAt) - now);
  const hours = Math.floor(difference / 3_600_000);
  const minutes = Math.floor((difference % 3_600_000) / 60_000);
  const seconds = Math.floor((difference % 60_000) / 1000);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function mailboxHeaders(token: string | null): HeadersInit {
  return token ? { "X-Mailbox-Token": token } : {};
}

function isGuestSession(value: GuestMailboxSession | Mailbox): value is GuestMailboxSession {
  return "mailbox" in value;
}

function randomDomainId(domains: readonly MailDomainSummary[], excludeId?: string): string {
  const candidates = domains.filter((domain) => domain.id !== excludeId);
  const pool = candidates.length > 0 ? candidates : domains;
  if (pool.length === 0) return "";
  const range = 0x1_0000_0000;
  const maximum = Math.floor(range / pool.length) * pool.length;
  const values = new Uint32Array(1);
  do crypto.getRandomValues(values); while ((values[0] ?? 0) >= maximum);
  return pool[(values[0] ?? 0) % pool.length]?.id ?? pool[0]?.id ?? "";
}

export function HomePage() {
  const { user, config, loading } = useSession();
  const toast = useToast();
  const [targetUrl, setTargetUrl] = useState("");
  const [createdLink, setCreatedLink] = useState<ShortLink | null>(null);
  const [recentLinks, setRecentLinks] = useState<ShortLink[]>(() => readRecentLinks());
  const [linkPending, setLinkPending] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [mailSession, setMailSession] = useState<StoredMailboxSession | null>(() => readStoredMailbox());
  const [workspace, setWorkspace] = useState<MailboxWorkspace | null>(null);
  const [mailLoading, setMailLoading] = useState(false);
  const [mailError, setMailError] = useState<string | null>(null);
  const [domainId, setDomainId] = useState("");
  const [alphanumericMailbox, setAlphanumericMailbox] = useState(readAlphanumericPreference);
  const [selectedMessage, setSelectedMessage] = useState<MailMessage | null>(null);
  const [now, setNow] = useState(0);
  const creatingRef = useRef(false);

  const activeDomainId = domainId || config?.mailDomains[0]?.id || "";

  useEffect(() => {
    const initial = window.setTimeout(() => setNow(Date.now()), 0);
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(timer);
    };
  }, []);

  const refreshMailbox = useCallback(async (session: StoredMailboxSession) => {
    const data = await api<MailboxWorkspace>(`/api/mailboxes/${session.id}`, {
      headers: mailboxHeaders(session.token),
    });
    setWorkspace(data);
    setMailError(null);
    return data;
  }, []);

  const createFreshMailbox = useCallback(async (selectedDomainId: string) => {
    if (creatingRef.current || !selectedDomainId) return;
    creatingRef.current = true;
    setMailLoading(true);
    setMailError(null);
    try {
      const result = await api<GuestMailboxSession | Mailbox>("/api/public/mailboxes", {
        method: "POST",
        ...jsonBody({
          domainId: selectedDomainId,
          permanent: false,
          addressStyle: alphanumericMailbox ? "alphanumeric" : "words",
        }),
      });
      const next: StoredMailboxSession = isGuestSession(result)
        ? { id: result.mailbox.id, token: result.accessToken, recoveryCode: result.recoveryCode }
        : { id: result.id, token: null, recoveryCode: null };
      saveStoredMailbox(next);
      setMailSession(next);
      await refreshMailbox(next);
    } catch (error) {
      setMailError(error instanceof Error ? error.message : "临时邮箱创建失败");
    } finally {
      creatingRef.current = false;
      setMailLoading(false);
    }
  }, [alphanumericMailbox, refreshMailbox]);

  useEffect(() => {
    if (loading || !config?.mailEnabled || !activeDomainId) return;
    let cancelled = false;
    const session = mailSession;
    const load = async () => {
      if (!session) {
        const selectedDomainId = domainId || randomDomainId(config.mailDomains);
        if (!domainId) setDomainId(selectedDomainId);
        await createFreshMailbox(selectedDomainId);
        return;
      }
      setMailLoading(true);
      try {
        const data = await refreshMailbox(session);
        if (cancelled) return;
        if (data.mailbox.domain) {
          const matching = config.mailDomains.find((domain) => domain.domain === data.mailbox.domain);
          if (matching) setDomainId(matching.id);
        }
      } catch {
        if (!cancelled) {
          sessionStorage.removeItem(guestSessionKey);
          setMailSession(null);
          const selectedDomainId = randomDomainId(config.mailDomains, activeDomainId);
          setDomainId(selectedDomainId);
          await createFreshMailbox(selectedDomainId);
        }
      } finally {
        if (!cancelled) setMailLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [activeDomainId, config, createFreshMailbox, domainId, loading, mailSession, refreshMailbox, user?.id]);

  useEffect(() => {
    if (!mailSession) return;
    const timer = window.setInterval(() => {
      void refreshMailbox(mailSession).catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [mailSession, refreshMailbox]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!mailSession || !workspace || workspace.mailbox.status === "released") return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [mailSession, workspace]);

  const newestCode = useMemo(
    () => workspace?.messages.find((message) => message.verificationCode) ?? null,
    [workspace],
  );

  const submitShortLink = async (event: FormEvent) => {
    event.preventDefault();
    setLinkPending(true);
    setLinkError(null);
    try {
      const link = await api<ShortLink>("/api/public/links", {
        method: "POST",
        ...jsonBody({ targetUrl }),
      });
      setCreatedLink(link);
      setRecentLinks(storeRecentLink(link));
    } catch (error) {
      setLinkError(error instanceof Error ? error.message : "短链生成失败");
    } finally {
      setLinkPending(false);
    }
  };

  const rotateMailbox = async (requestedDomainId?: string) => {
    const nextDomainId = requestedDomainId || randomDomainId(config?.mailDomains ?? [], activeDomainId);
    if (!nextDomainId) return;
    setSelectedMessage(null);
    setDomainId(nextDomainId);
    if (mailSession) {
      await api<{ released: true }>(`/api/mailboxes/${mailSession.id}`, {
        method: "DELETE",
        headers: mailboxHeaders(mailSession.token),
      }).catch(() => undefined);
    }
    sessionStorage.removeItem(guestSessionKey);
    setMailSession(null);
    setWorkspace(null);
    await createFreshMailbox(nextDomainId);
  };

  const openMessage = async (id: string) => {
    if (!mailSession) return;
    try {
      const message = await api<MailMessage>(`/api/mailboxes/${mailSession.id}/messages/${id}`, {
        headers: mailboxHeaders(mailSession.token),
      });
      setSelectedMessage(message);
      await api<{ readAt: string }>(`/api/mailboxes/${mailSession.id}/messages/${id}/read`, {
        method: "PATCH",
        headers: mailboxHeaders(mailSession.token),
      });
      await refreshMailbox(mailSession);
    } catch (error) {
      toast.fromError(error);
    }
  };

  const loadAttachment = async (attachment: MailAttachment): Promise<Blob | undefined> => {
    if (!mailSession) return;
    const response = await fetch(attachment.downloadUrl, {
      credentials: "same-origin",
      headers: mailboxHeaders(mailSession.token),
    });
    if (!response.ok) throw new Error("附件下载失败");
    return response.blob();
  };

  const downloadAttachment = async (attachment: MailAttachment) => {
    const blob = await loadAttachment(attachment);
    if (!blob) return;
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = attachment.filename;
    anchor.click();
    URL.revokeObjectURL(href);
  };

  const downloadQr = () => {
    const node = document.querySelector("#home-link-qr svg");
    if (!(node instanceof SVGElement) || !createdLink) return;
    const blob = new Blob([new XMLSerializer().serializeToString(node)], { type: "image/svg+xml" });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = `g2al-${createdLink.slug}.svg`;
    anchor.click();
    URL.revokeObjectURL(href);
  };

  if (loading) return <main className="public-shell public-shell--center"><Loading label="正在连接 g2.al" /></main>;

  return (
    <main className="home-page">
      <header className="home-nav">
        <Link to="/" className="wordmark">{config?.appName ?? "g2.al"}</Link>
        <a href="#shortener" aria-current="page">短链 <span>links</span></a>
        <a href="#temporary-mail">临时邮箱 <span>mail</span></a>
        <a href="#pricing">价格</a>
        <Tag tone="outline">内测中 · PRIVATE BETA</Tag>
        <Link className="home-nav__account" to={user ? "/console/links" : "/login"}>
          {user ? "进入控制台" : "登录"} <ArrowRight size={14} />
        </Link>
      </header>

      <section className="home-intro">
        <div>
          <h1>一个字符的链接，<br />一分钟的邮箱。</h1>
          <p>g2.al 提供极短跳转域名与一次性收件地址。不注册也能用：短链即刻生成，临时邮箱开箱即收——停在本页就能看到新邮件。</p>
        </div>
        <div className="home-facts">
          <div><strong>1 位</strong><span>最短 slug</span></div>
          <div><strong>{config?.mailDefaultTtlHours ?? 24} 小时</strong><span>邮箱默认时效</span></div>
          <div><strong>{config?.mailDomains.length ?? 0} 个</strong><span>可选域名</span></div>
        </div>
      </section>

      <section className="home-workbench">
        <article id="shortener" className="shortener-bench">
          <header className="bench-title"><h2>短链</h2><span>shorten a url</span></header>
          <form className="home-inline-form" onSubmit={(event) => void submitShortLink(event)}>
            <input type="url" value={targetUrl} onChange={(event) => setTargetUrl(event.target.value)} placeholder="https://example.com/very/long/url" required aria-label="需要缩短的网址" />
            <Button variant="primary" type="submit" disabled={linkPending}>{linkPending ? "生成中" : "生成"}</Button>
          </form>
          <div className="bench-hint"><Tag tone="outline">自定义 slug 需登录</Tag><span>未登录生成 6 位随机后缀，有效期 7 天</span></div>
          {linkError ? <div className="inline-error" role="alert">{linkError}</div> : null}
          {createdLink ? (
            <section className="created-link-card">
              <div>
                <span className="eyebrow">刚刚生成 · JUST CREATED</span>
                <strong>{createdLink.shortUrl.replace(/^https?:\/\//, "")}</strong>
                <small>{createdLink.targetUrl}</small>
                <div>
                  <Button variant="primary" onClick={() => void copyText(createdLink.shortUrl).then(() => toast.push("短链已复制"))}><Copy size={13} />复制</Button>
                  <Button onClick={downloadQr}><Download size={13} />下载二维码</Button>
                </div>
              </div>
              <div id="home-link-qr" className="created-link-card__qr"><QRCodeSVG value={createdLink.shortUrl} size={108} level="M" /></div>
            </section>
          ) : (
            <section className="shortener-placeholder"><span className="eyebrow">READY · 即刻生成</span><strong>粘贴一个完整网址。</strong><p>生成后可直接复制，二维码会同步出现。</p></section>
          )}
          <div className="recent-local">
            <span className="eyebrow">RECENT · 本机最近</span>
            {recentLinks.length ? recentLinks.map((link) => (
              <button type="button" key={link.id} onClick={() => void copyText(link.shortUrl).then(() => toast.push("短链已复制"))}>
                <strong>{link.shortUrl.replace(/^https?:\/\//, "")}</strong><span>{link.targetUrl}</span><small>7D</small>
              </button>
            )) : <p>还没有本机记录。</p>}
          </div>
        </article>

        <article id="temporary-mail" className="mail-bench">
          <header className="mail-address-panel">
            <div className="bench-title"><h2>临时邮箱</h2><span>disposable inbox</span></div>
            {workspace ? (
              <div className="mail-address-control">
                <strong>{workspace.mailbox.localPart}</strong><span>@</span>
                <select value={activeDomainId} aria-label="临时邮箱域名" onChange={(event) => { const value = event.target.value; setDomainId(value); void rotateMailbox(value); }}>
                  {config?.mailDomains.map((domain) => <option value={domain.id} key={domain.id}>{domain.domain}</option>)}
                </select>
                <Button variant="primary" onClick={() => void copyText(workspace.mailbox.address).then(() => toast.push("邮箱地址已复制"))}>复制</Button>
              </div>
            ) : <div className="mail-address-loading">{mailLoading ? <Loading label="正在分配地址" /> : "暂无可用地址"}</div>}
            <div className="mail-session-meta">
              {workspace ? <Tag tone="accent">剩余 {remainingLabel(workspace.mailbox.expiresAt, now)}</Tag> : null}
              <label className="mail-style-choice" title="该选项在下次分配地址时生效">
                <input
                  type="checkbox"
                  checked={alphanumericMailbox}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setAlphanumericMailbox(checked);
                    try { localStorage.setItem(mailboxStyleKey, String(checked)); } catch { /* Preference is optional. */ }
                  }}
                />
                <span>下次地址不含 -</span>
              </label>
              <button type="button" onClick={() => void rotateMailbox()} disabled={mailLoading}><RefreshCw size={12} />换一个</button>
              <Link to={user ? "/console/mail" : "/login"}>延长 +{config?.mailDefaultTtlHours ?? 24}h（需登录）</Link>
              <span className="live-state"><i />实时接收中 LIVE</span>
            </div>
          </header>

          {newestCode ? (
            <button className="verification-ribbon" type="button" onClick={() => void copyText(newestCode.verificationCode ?? "").then(() => toast.push("验证码已复制"))}>
              <span className="sender-mark">{(newestCode.senderName || newestCode.senderAddress).slice(0, 1).toUpperCase()}</span>
              <span><strong>{newestCode.senderName || newestCode.senderAddress} · 验证码</strong><small>{newestCode.subject}</small></span>
              <code>{newestCode.verificationCode}</code>
              <span className="verification-copy"><Copy size={13} />复制验证码</span>
            </button>
          ) : null}

          <div className="home-inbox-table">
            <div className="home-inbox-head"><span>发件人 FROM</span><span>主题 / 验证码</span><span>时间</span></div>
            {mailError ? <div className="mail-empty"><ShieldCheck size={18} /><strong>{mailError}</strong><button type="button" onClick={() => void createFreshMailbox(activeDomainId)}>重试</button></div> : null}
            {!mailError && workspace?.messages.length ? workspace.messages.slice(0, 6).map((message) => (
              <button className={message.readAt ? "mail-row" : "mail-row mail-row--unread"} type="button" key={message.id} onClick={() => void openMessage(message.id)}>
                <span>{message.senderName || message.senderAddress}</span>
                <span className="mail-row__subject"><span>{message.subject}</span>{message.verificationCode ? <code>{message.verificationCode}</code> : null}</span>
                <time>{new Date(message.createdAt).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })}</time>
              </button>
            )) : null}
            {!mailError && workspace && !workspace.messages.length ? <div className="mail-empty"><Inbox size={24} /><strong>收件箱正在监听</strong><span>把上方地址填到需要收信的服务，邮件到达后会自动出现。</span></div> : null}
            {mailLoading && !workspace ? <div className="mail-empty"><Loading label="连接 Cloudflare 邮件路由" /></div> : null}
          </div>

          <footer className="mail-permission-bar">
            <LockKeyhole size={16} />
            <div><strong>{user ? "登录地址可进入完整工作台" : "未登录仅可收信，发送需登录"}</strong><span>{mailSession?.recoveryCode ? `恢复码：${mailSession.recoveryCode}（仅显示于本次浏览器会话）` : "域组与发信权限由管理员统一控制。"}</span></div>
            <Link className="button" to={user ? "/console/mail" : "/login"}>{user ? "打开邮箱工作台" : "登录以发信"}</Link>
          </footer>
        </article>
      </section>

      <Dialog key={selectedMessage?.id ?? "mail-reader"} open={Boolean(selectedMessage)} onClose={() => setSelectedMessage(null)} title="邮件详情" eyebrow="INBOX · 安全阅读" reader>
        {selectedMessage ? (
          <MailMessageReader
            key={selectedMessage.id}
            message={selectedMessage}
            onCopyCode={(code) => void copyText(code).then(() => toast.push("验证码已复制"))}
            onLoadInlineAttachment={async (attachment) => {
              const blob = await loadAttachment(attachment);
              if (!blob) throw new Error("内嵌图片加载失败");
              return blob;
            }}
            onDownloadAttachment={(attachment) => void downloadAttachment(attachment).catch((error) => toast.fromError(error))}
          />
        ) : null}
      </Dialog>

      <section id="pricing" className="home-beta-strip">
        <div><strong>开放注册前，<br />先拿一个不留痕迹的地址。</strong></div>
        <p>注册后可绑定更多域组、设置永不过期、按用户组开通发信能力。匿名地址默认只保留一个浏览器会话。</p>
        <Link className="button" to={config?.registrationOpen ? "/register" : "/login"}>{config?.registrationOpen ? "申请内测名额" : "内部登录"}<ArrowRight size={14} /></Link>
      </section>

      <footer className="home-footer"><span><Clock3 size={13} />邮箱到期后进入 24 小时宽限，再释放并清理内容。</span><span><Mail size={13} />Cloudflare Email Routing</span></footer>
    </main>
  );
}
